import csv from 'jquery-csv'
import moment from 'moment'

// Accepted CSV header aliases (lower-cased, trimmed) for the EUDR tickets-grid export
// (Tract, Latitude, Longitude, Harvest Start Date, Harvest End Date).
const COLUMN_ALIASES = {
  tract: ['tract', 'tract name', 'tractname'],
  latitude: ['latitude', 'lat'],
  longitude: ['longitude', 'long', 'lng', 'lon'],
  harvestStart: ['harvest start date', 'harveststartdate', 'harvest start'],
  harvestEnd: ['harvest end date', 'harvestenddate', 'harvest end']
}

const DATE_FORMATS = ['MM/DD/YYYY', moment.ISO_8601]

const resolveColumnIndexes = (headerRow) => {
  const normalized = headerRow.map(h => (h ?? '').trim().toLowerCase())
  const indexes = {}
  for (const [key, aliases] of Object.entries(COLUMN_ALIASES)) {
    indexes[key] = normalized.findIndex(h => aliases.includes(h))
  }
  return indexes
}

const parseDate = (raw) => {
  if (!raw) return null
  const m = moment(raw.trim(), DATE_FORMATS, true)
  return m.isValid() ? m.format('YYYY-MM-DD') : null
}

/**
 * Builds an EUDR GeoJSON FeatureCollection of Point features from a lat/long CSV, deliberately
 * without any parcel lookup — points are reconciled against polygon shapes later, during review.
 * Rows group into one point per tract name (first valid coordinate wins), or by rounded coordinate
 * when tractless. The synthetic `point:<lon>,<lat>` parcelId keeps features upload- and
 * composite-eligible without colliding with real ReportAll robust_ids.
 * @returns {{ geojson: object, tractCount: number, pointCount: number, duplicateCount: number, unresolved: Array }}
 */
export function buildEudrGeoJsonFromCsv (csvText) {
  const rows = csv.toArrays(csvText).filter(r => r.some(cell => (cell ?? '').trim().length > 0))
  if (rows.length < 2) throw new Error('EUDR_CSV_EMPTY')

  const cols = resolveColumnIndexes(rows[0])
  if (cols.latitude < 0 || cols.longitude < 0) throw new Error('EUDR_CSV_HEADERS')

  // One group per tract name; tractless rows key on their rounded coordinate instead (the '@' prefix
  // cannot collide with a tract name group since those keys are the names themselves).
  const groups = new Map()
  const unresolved = []
  rows.slice(1).forEach((row, index) => {
    const tract = cols.tract >= 0 ? (row[cols.tract] ?? '').trim() : ''
    if (tract) {
      if (!groups.has(tract)) groups.set(tract, { tract, rows: [] })
      groups.get(tract).rows.push(row)
      return
    }
    const latitudeText = String(row[cols.latitude] ?? '').trim()
    const longitudeText = String(row[cols.longitude] ?? '').trim()
    // A tractless row with BLANK coordinate cells is spreadsheet residue (Excel ghost rows keep stray
    // content — a date, a total — long after the data was deleted), not a location that failed: skip it
    // silently. Rows that carry coordinate text that doesn't parse are still reported as skipped.
    if (!latitudeText && !longitudeText) return
    const latitude = parseFloat(latitudeText)
    const longitude = parseFloat(longitudeText)
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      // +2: 1-based line numbers, past the header row.
      unresolved.push({ tract: `row ${index + 2}`, reason: 'invalidCoordinates' })
      return
    }
    const key = `@${longitude.toFixed(6)},${latitude.toFixed(6)}`
    if (!groups.has(key)) groups.set(key, { tract: null, rows: [] })
    groups.get(key).rows.push(row)
  })
  // Locations attempted: every group plus the tractless rows that never formed one — the denominator
  // for the "{count} of {total} skipped" notice.
  const attemptedCount = groups.size + unresolved.length

  const features = []

  for (const { tract, rows: groupRows } of groups.values()) {
    const coordRow = groupRows
      .map(r => ({ latitude: parseFloat(r[cols.latitude]), longitude: parseFloat(r[cols.longitude]) }))
      .find(c => Number.isFinite(c.latitude) && Number.isFinite(c.longitude))

    if (!coordRow) {
      unresolved.push({ tract, reason: 'invalidCoordinates' })
      continue
    }

    const starts = cols.harvestStart >= 0 ? groupRows.map(r => parseDate(r[cols.harvestStart])).filter(Boolean) : []
    const ends = cols.harvestEnd >= 0 ? groupRows.map(r => parseDate(r[cols.harvestEnd])).filter(Boolean) : []

    // Match the function-app agent: coordinates rounded to 6 decimal places (~0.1m).
    const longitude = Number(coordRow.longitude.toFixed(6))
    const latitude = Number(coordRow.latitude.toFixed(6))

    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [longitude, latitude] },
      properties: {
        // Null for tractless rows: display falls back to the coordinates, never the synthetic id.
        productionPlace: tract,
        parcelId: `point:${longitude},${latitude}`,
        harvestStart: starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : null,
        harvestEnd: ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : null,
        geometrySource: 'point',
        uploadKind: 'csvPoint'
      }
    })
  }

  // Merge same-coordinate locations; otherwise the composite flags them contested over one parcelId.
  const byParcelId = new Map()
  let duplicateCount = 0
  for (const feature of features) {
    const existing = byParcelId.get(feature.properties.parcelId)
    if (!existing) {
      byParcelId.set(feature.properties.parcelId, feature)
      continue
    }
    duplicateCount++
    const kept = existing.properties
    const dupe = feature.properties
    kept.productionPlace = kept.productionPlace ?? dupe.productionPlace
    kept.harvestStart = [kept.harvestStart, dupe.harvestStart].filter(Boolean).sort()[0] ?? null
    kept.harvestEnd = [kept.harvestEnd, dupe.harvestEnd].filter(Boolean).sort().pop() ?? null
  }

  const deduped = [...byParcelId.values()].sort((a, b) =>
    (b.geometry.coordinates[1] - a.geometry.coordinates[1]) ||
    (a.geometry.coordinates[0] - b.geometry.coordinates[0]))

  return {
    geojson: { type: 'FeatureCollection', features: deduped },
    tractCount: attemptedCount,
    pointCount: deduped.length,
    duplicateCount,
    unresolved
  }
}
